import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import ChangePasswordModal from './ChangePasswordModal';

const BACKEND_URL = process.env.REACT_APP_API_URL;

const SideActionDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [checkingOut, setCheckingOut] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [supportReason, setSupportReason] = useState('');
  const [supportSubcategory, setSupportSubcategory] = useState('');
  const [supportQuestion, setSupportQuestion] = useState('');
  const [submittingSupport, setSubmittingSupport] = useState(false);
  const [supportSuccess, setSupportSuccess] = useState(false);

  const todayStr = new Date().toISOString().split('T')[0];
  const isSubscriber =
    !!user?.isSubscriber &&
    (!user?.subscriptionExpiration || user.subscriptionExpiration >= todayStr);

  const handleSubscribeClick = async () => {
    if (!user) return;
    setCheckingOut(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/bookie-subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          bookieId: user.id,
          bookieEmail: user.email,
          bookieName: user.name,
        }),
      });
      const data = await response.json();
      if (data.success && data.stripeUrl) {
        window.location.href = data.stripeUrl;
      }
    } catch (error) {
      console.error('Checkout error:', error);
    } finally {
      setCheckingOut(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSupportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supportReason || !supportQuestion.trim()) return;
    setSubmittingSupport(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/support/submit/bookie`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          reason: supportReason,
          category: supportReason,
          subcategory: supportSubcategory || null,
          questions: supportQuestion,
        }),
      });
      if (response.ok) {
        setSupportSuccess(true);
        setTimeout(() => {
          setShowSupportModal(false);
          setSupportReason('');
          setSupportSubcategory('');
          setSupportQuestion('');
          setSupportSuccess(false);
        }, 1500);
      }
    } catch (error) {
      console.error('Support submit error:', error);
    } finally {
      setSubmittingSupport(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900">
      {/* Floating Support Button */}
      <button
        className="fixed bottom-5 right-6 z-50 flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur-md border border-white/30 text-white text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg transition duration-200"
        onClick={() => setShowSupportModal(true)}
        title="Contact Support 24/7"
      >
        <span className="text-base">🎧</span>
        <span>Contact Support 24/7</span>
      </button>

      {/* Support Modal */}
      {showSupportModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={(e) => { if (e.target === e.currentTarget) setShowSupportModal(false); }}
        >
          <div
            className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-lg w-full mx-4 p-8"
            style={{ maxHeight: '80vh', overflowY: 'auto' }}
          >
            <h2 className="text-2xl font-bold text-white mb-2">Contact Support 24/7</h2>
            <p className="text-emerald-200 mb-6">We're here to help. Send us your question and we'll get back to you.</p>

            {supportSuccess ? (
              <div className="text-center py-8">
                <span className="text-5xl">✅</span>
                <p className="text-green-300 text-lg font-semibold mt-4">Ticket submitted successfully!</p>
              </div>
            ) : (
              <form onSubmit={handleSupportSubmit} className="space-y-4">
                <div>
                  <label className="block text-emerald-200 text-sm font-semibold mb-1">Reason for contacting support</label>
                  <select
                    value={supportReason}
                    onChange={(e) => { setSupportReason(e.target.value); setSupportSubcategory(''); }}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-400"
                    required
                  >
                    <option value="" className="bg-emerald-900">-- Select a reason --</option>
                    <option value="Need help with bowler account creation" className="bg-emerald-900">Need help with bowler account creation</option>
                    <option value="Need help Creating a League" className="bg-emerald-900">Need help Creating a League</option>
                    <option value="Need Help with side action management" className="bg-emerald-900">Need Help with side action management</option>
                  </select>
                </div>

                {supportReason === 'Need Help with side action management' && (
                  <div>
                    <label className="block text-emerald-200 text-sm font-semibold mb-1">Subcategory</label>
                    <select
                      value={supportSubcategory}
                      onChange={(e) => setSupportSubcategory(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-400"
                    >
                      <option value="" className="bg-emerald-900">-- Select a subcategory --</option>
                      <option value="Question/issue with adding side events" className="bg-emerald-900">Question/issue with adding side events</option>
                      <option value="Question/issue with managing a side event" className="bg-emerald-900">Question/issue with managing a side event</option>
                      <option value="Question/issue with the financials" className="bg-emerald-900">Question/issue with the financials</option>
                      <option value="Question/issues with printing" className="bg-emerald-900">Question/issues with printing</option>
                      <option value="Question/issues with Brackets specifically" className="bg-emerald-900">Question/issues with Brackets specifically</option>
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-emerald-200 text-sm font-semibold mb-1">Your question or issue</label>
                  <textarea
                    value={supportQuestion}
                    onChange={(e) => setSupportQuestion(e.target.value)}
                    rows={4}
                    placeholder="Describe your issue or question in detail..."
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-emerald-400 resize-none"
                    required
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowSupportModal(false)}
                    className="flex-1 bg-white/10 hover:bg-white/20 text-white font-semibold py-2 rounded-lg transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingSupport || !supportReason || !supportQuestion.trim()}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2 rounded-lg transition-all disabled:opacity-50"
                  >
                    {submittingSupport ? 'Submitting...' : 'Submit Ticket'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center py-4 sm:py-6 gap-3">
            {/* User Info */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-base sm:text-lg">
                  {user.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                  <h1 className="text-lg sm:text-2xl font-bold text-white">
                    {t('bookie.dashboard.welcome', { name: user.name })}
                  </h1>
                  {/* Bookie badge */}
                  <div className="flex items-center space-x-1 bg-gradient-to-r from-emerald-500 to-teal-600 px-2 sm:px-3 py-1 rounded-full">
                    <span className="text-white text-xs sm:text-sm font-semibold">
                      <span className="hidden sm:inline">🪙 </span>{t('bookie.dashboard.bookie')}
                    </span>
                  </div>
                  {/* Subscription badge */}
                  {isSubscriber ? (
                    <div className="flex items-center bg-gradient-to-r from-yellow-400 to-yellow-500 px-2 sm:px-3 py-1 rounded-full">
                      <span className="text-yellow-900 text-xs sm:text-sm font-semibold">👑 {t('bookie.dashboard.premiumMember')}</span>
                    </div>
                  ) : (
                    <button
                      onClick={handleSubscribeClick}
                      disabled={checkingOut}
                      className="flex items-center bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 px-2 sm:px-3 py-1 rounded-full transition duration-200 disabled:opacity-50"
                    >
                      <span className="text-white text-xs sm:text-sm font-semibold">
                        {checkingOut ? t('bookie.subscription.processing') : `⭐ ${t('bookie.dashboard.becomeSubscriber')}`}
                      </span>
                    </button>
                  )}
                </div>
                <p className="text-emerald-200 text-xs sm:text-sm">
                  {t('bookie.dashboard.subtitle')}
                </p>
              </div>
            </div>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="group flex items-center space-x-2 bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 px-3 py-2 sm:px-6 sm:py-3 rounded-lg transition-all duration-200 transform hover:scale-105"
            >
              <span className="hidden sm:inline text-white font-semibold">{t('common.logout')}</span>
              <svg className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid gap-8">

          {/* Welcome Card */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20">
            <div className="text-center">
              <h2 className="text-4xl font-bold text-white mb-2">{t('bookie.dashboard.title')}</h2>

            </div>
          </div>

          {/* Subscription Banner — only shown when NOT subscribed */}
          {!isSubscriber && (
            <div className="bg-orange-900/30 backdrop-blur-lg rounded-xl border-2 border-orange-400/60 p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">{t('bookie.subscription.title')}</h3>
                  <p className="text-orange-200 mb-4">{t('bookie.subscription.subtitle')}</p>
                  <div className="space-y-1 text-sm text-orange-100">
                    <p>✅ {t('bookie.subscription.benefit1')}</p>
                    <p>✅ {t('bookie.subscription.benefit2')}</p>
                    <p>✅ {t('bookie.subscription.benefit3')}</p>
                  </div>
                </div>
                <div className="text-center shrink-0">
                  {/* Crossed-out monthly price */}
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <span className="text-4xl font-bold text-red-400 line-through opacity-70">$40</span>
                    <span className="text-orange-300 text-sm line-through opacity-70">/month</span>
                  </div>
                  {/* Actual yearly price */}
                  <div className="flex items-end justify-center gap-1 mb-1">
                    <span className="text-orange-300 text-sm font-semibold">only</span>
                    <span className="text-md font-extrabold text-white leading-none">$360</span>
                  </div>
                  <p className="text-orange-200 text-sm mb-2">one-time yearly payment</p>
                  {/* Savings badge */}
                  <div className="inline-block bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-4 animate-pulse">
                    🎉 $120 OFF vs monthly!
                  </div>
                  <br />
                  <button
                    onClick={handleSubscribeClick}
                    disabled={checkingOut}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-8 py-3 rounded-xl transition duration-200 disabled:opacity-50"
                  >
                    {checkingOut ? t('bookie.subscription.processing') : t('bookie.subscription.subscribeButton')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Action Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            {/* Start New League Side Action */}
            <div
              className={`bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 transition duration-300 ${isSubscriber
                  ? 'hover:bg-white/15 cursor-pointer group'
                  : 'opacity-60 cursor-not-allowed'
                }`}
              title={!isSubscriber ? t('bookie.dashboard.premiumFeature') : undefined}
              onClick={isSubscriber ? () => navigate('/bookie/create-league') : undefined}
            >
              <div className="text-center">
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-200">
                  {isSubscriber ? '🎯' : '🚫'}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {t('bookie.dashboard.startSideAction.title')}
                </h3>
                <p className="text-emerald-200 text-sm">
                  {t('bookie.dashboard.startSideAction.description')}
                </p>
                {!isSubscriber && (
                  <p className="text-orange-300 text-xs mt-3 font-medium">
                    {t('bookie.dashboard.premiumFeature')}
                  </p>
                )}
              </div>
            </div>

            {/* Manage League Side Action */}
            <div
              onClick={() => isSubscriber && navigate('/bookie/manage-league')}
              className={`bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 transition duration-300 ${isSubscriber
                  ? 'hover:bg-white/15 cursor-pointer group'
                  : 'opacity-60 cursor-not-allowed'
                }`}
              title={!isSubscriber ? t('bookie.dashboard.premiumFeature') : undefined}
            >
              <div className="text-center">
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-200">
                  {isSubscriber ? '📋' : '🚫'}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {t('bookie.dashboard.manageSideAction.title')}
                </h3>
                <p className="text-emerald-200 text-sm">
                  {t('bookie.dashboard.manageSideAction.description')}
                </p>
                {!isSubscriber && (
                  <p className="text-orange-300 text-xs mt-3 font-medium">
                    {t('bookie.dashboard.premiumFeature')}
                  </p>
                )}
                {isSubscriber && (
                  <p className="text-emerald-400 text-xs mt-2 font-medium">
                    {t('bookie.dashboard.manageSideAction.cta', 'View Leagues →')}
                  </p>
                )}
              </div>
            </div>

            {/* Create Bowler Accounts */}
            <div
              className={`bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 transition duration-300 ${isSubscriber
                  ? 'hover:bg-white/15 cursor-pointer group'
                  : 'opacity-60 cursor-not-allowed'
                }`}
              title={!isSubscriber ? t('bookie.dashboard.premiumFeature') : undefined}
              onClick={isSubscriber ? () => navigate('/bookie/create-bowler') : undefined}
            >
              <div className="text-center">
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-200">
                  {isSubscriber ? '👤' : '🚫'}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {t('bookie.dashboard.createBowlers.title')}
                </h3>
                <p className="text-emerald-200 text-sm">
                  {t('bookie.dashboard.createBowlers.description')}
                </p>
                {!isSubscriber && (
                  <p className="text-orange-300 text-xs mt-3 font-medium">
                    {t('bookie.dashboard.premiumFeature')}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* youtube tutorial */}
          <div className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <div className="bg-red-100 rounded-lg p-1.5 flex-shrink-0">
                <svg className="w-4 h-4 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                </svg>
              </div>
              <h2 className="text-sm font-bold text-white uppercase tracking-widest">Side Action Tutorials</h2>
            </div>
            <div className="bg-gray-800 rounded-2xl shadow-lg border border-gray-500 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <p className="text-sm text-white mt-0.5">Find everything you need on the youtube playlist "Side Action Manager" to learn how to use the system!</p>
              </div>
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/amSMvBfs2bA"
                  title="Side Action Tutorial"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>

          {/* Account Info */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">{t('bookie.dashboard.accountInfo')}</h3>
              <button
                onClick={() => setShowChangePasswordModal(true)}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-2 rounded-lg transition group"
              >
                <svg className="w-6 h-6 text-white/60 group-hover:rotate-45 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-sm font-semibold text-white/70 group-hover:text-white transition">Change Password</span>
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4 text-emerald-200">
              <div>
                <p><strong className="text-white">{t('auth.email')}:</strong> {user.email}</p>
                <p><strong className="text-white">{t('bookie.dashboard.role')}:</strong> {t('bookie.dashboard.bookie')}</p>
              </div>
              <div>
                <p>
                  <strong className="text-white">{t('bookie.dashboard.subscriptionStatus')}:</strong>{' '}
                  {isSubscriber
                    ? <span className="text-green-400">{t('bookie.dashboard.premiumMember')}</span>
                    : <span className="text-orange-400">{t('bookie.dashboard.freeAccount')}</span>
                  }
                </p>
                {user.subscriptionExpiration && isSubscriber && (
                  <p>
                    <strong className="text-white">{t('bookie.dashboard.expiresOn')}:</strong>{' '}
                    {new Date(user.subscriptionExpiration).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {showChangePasswordModal && (
        <ChangePasswordModal accentColor="emerald" onClose={() => setShowChangePasswordModal(false)} />
      )}
    </div>
  );
};

export default SideActionDashboard;
